import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const gradeColor = (g) => {
  if(!g) return "#8c92b0";
  if(g.startsWith("A")) return "var(--success)";
  if(g.startsWith("B")) return "#4e9af1";
  if(g.startsWith("C")) return "var(--warning)";
  return "var(--grade-f)";
};

const riskColor = (r) => {
  if(r === "VERIFIED_SAFE") return "#a855f7";
  if(r === "LOW")           return "var(--success)";
  if(r === "MEDIUM")        return "var(--warning)";
  if(r === "HIGH")          return "var(--warning)";
  if(r === "EXTREME")       return "var(--grade-f)";
  return "#8c92b0";
};

const riskLabel = (r) => {
  if(r === "VERIFIED_SAFE") return "Safe Risk";
  if(r === "LOW")           return "Low Risk";
  if(r === "MEDIUM")        return "Medium Risk";
  if(r === "HIGH")          return "High Risk";
  if(r === "EXTREME")       return "Extreme Risk";
  return r || "—";
};

const strategyStory = (t) => {
  const roi = t.roi || 0;
  const dd = t.maxDD || t.max_drawdown_pct || 0;
  const pf = t.profitFactor || t.profit_factor || 0;
  const wr = t.winRate || t.win_rate || 0;
  const risk = t.riskLevel || t.risk_level || "";
  const trades = t.totalTrades || t.total_trades || 0;

  // Investor type dari risk level
  const investorType = risk === "VERIFIED_SAFE" ? "Capital-Preserving Investors"
    : risk === "LOW"    ? "Conservative Investors"
    : risk === "MEDIUM" ? "Balanced Investors"
    : risk === "HIGH"   ? "Growth-Seeking Investors"
    : risk === "EXTREME"? "High-Risk Tolerant Investors"
    : "All Investors";

  // Risk tag
  const riskTag = risk === "VERIFIED_SAFE" ? "Very Low Risk"
    : risk === "LOW"    ? "Low Risk"
    : risk === "MEDIUM" ? "Medium Risk"
    : risk === "HIGH"   ? "High Risk"
    : risk === "EXTREME"? "Extreme Risk"
    : "Risk: TBD";

  // Consistency dari win rate + profit factor
  const consistTag = trades < 20 ? "Building Track Record"
    : (wr >= 65 && pf >= 2.0) ? "Proven Consistency"
    : (wr >= 55 || pf >= 1.5) ? "Consistent Performance"
    : "Developing";

  return { investorType, riskTag, consistTag };
};


const scoreDesc = (score) => {
  if (score >= 85) return "Elite performer";
  if (score >= 70) return "Strong track record";
  if (score >= 50) return "Developing consistency";
  if (score >= 30) return "Early stage trader";
  return "Limited data";
};

const investorType = (riskLevel) => {
  if(riskLevel === "VERIFIED_SAFE" || riskLevel === "LOW") return "Suited for Conservative Investors";
  if(riskLevel === "MEDIUM") return "Suited for Balanced Investors";
  if(riskLevel === "HIGH") return "Suited for Growth-Seeking Investors";
  return "Suited for High-Risk Tolerant Investors";
};
const signalStyle = (avgWeek, sourceType) => {
  const isTrader = sourceType === "live_trader" || sourceType === "signal_sync";
  const noun = isTrader ? "Trades" : "Signals";
  if(avgWeek >= 5) return `Active ${noun}`;
  if(avgWeek >= 2) return `Moderate ${noun}`;
  return `Selective ${noun}`;
};
// Source type badge
const sourceTypeBadge = (t) => {
  if (t === "signal_sync")   return { label: "   SignalSync™", color: "var(--accent)",   bg: "rgba(78,154,241,0.12)",  border: "rgba(78,154,241,0.25)" };
  if (t === "live_trader")   return { label: "   Live Trader", color: "var(--success)",  bg: "rgba(34,197,94,0.1)",   border: "rgba(34,197,94,0.25)" };
  return { label: "   Analyst", color: "var(--text-muted)", bg: "var(--bg-elevated)", border: "var(--border-subtle)" };
};

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token") || localStorage.getItem("token") || ""}`
});

export default function MarketplacePage() {
  const [sets, setSets]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [affCode, setAffCode] = React.useState("");
  const [search, setSearch]       = useState("");
  const [sortBy, setSortBy]       = useState("alphaScore");
  const [analystPage, setAnalystPage] = useState(1);
  const ANALYST_PAGE_SIZE = 12;
  const [tab, setTab]             = useState("signals");
  const [signalType, setSignalType] = useState("ALL");
  const navigate                  = useNavigate();
  const [traders, setTraders]     = useState([]);
  const [copySubs, setCopySubs]   = useState([]);
  const [copyModal, setCopyModal] = useState(null);
  const [copyBusy, setCopyBusy]   = useState(null);
  const [traderSearch, setTraderSearch] = useState("");
  const [traderSort, setTraderSort]     = useState("alpha_score");
  const [traderRisk, setTraderRisk]     = useState("ALL");
  const [traderPlatform, setTraderPlatform] = useState("ALL");
  const [traderPage, setTraderPage]     = useState(1);
  const [traderTotal, setTraderTotal]   = useState(0);
  const [traderTotalPages, setTraderTotalPages] = useState(1);
  const [traderLoading, setTraderLoading] = useState(false);
  const [copyForm, setCopyForm]   = useState({lotMode:"FIXED",lotSize:0.01,riskPct:1,maxLot:1,copySl:true,copyTp:true});
  const [toast, setToast]         = useState(null);

  // CORE Portfolio Analyzer state — additive, does not touch existing state.
  // portfolioCart holds ONLY trader-type providers (live_trader/signal_sync)
  // for now; analyst signal sets are intentionally excluded until a backend
  // module exists to reconstruct their virtual daily equity series (see
  // AnalystDashboard.jsx's VirtualHeatmap, which currently computes this
  // client-side only, on-demand, from analyst_signals — not persisted to a
  // queryable daily series the way trader equity_snapshots is).
  const [portfolioCart, setPortfolioCart] = useState([]); // [{accountId, name}]
  const [analyzeModalOpen, setAnalyzeModalOpen] = useState(false);
  const [analyzeResult, setAnalyzeResult] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);

  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  useEffect(()=>{
    const tok = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    if (tok) {
      fetch("/api/investor/affiliate/overview",{headers:{Authorization:`Bearer ${tok}`}})
        .then(r=>r.json())
        .then(d=>{ if(d.ok && d.affiliate?.code) setAffCode(d.affiliate.code); })
        .catch(()=>{});
    }
  },[]);

  useEffect(()=>{
    // Fetch both signal sets + traders, normalize to unified format
    Promise.all([
      fetch("/api/public/marketplace").then(r=>r.json()),
      fetch("/api/public/traders?limit=50&sort=alpha_score").then(r=>r.json()),
    ]).then(([sigRes, traderRes]) => {
      const signals = (sigRes.sets||[]).map(s => ({
        ...s,
        _type: s.sourceType || "manual_analyst",
        alphaScore: Math.max(s.virtualAlphaScore||0, s.alphaScore||0),
        roi: s.virtualRoiPct || 0,
        maxDD: s.virtualMaxDDPct || 0,
        winRate: s.winRate || 0,
        subscribers: s.subscribers || 0,
        _id: "sig_" + s.id,
        _setId: s.id,
      }));
      const traders = (traderRes.traders||[]).map(t => {
        const ssActive = t.signalSyncStatus === "ACTIVE";
        return {
        _type: ssActive ? "signal_sync" : "live_trader",
        _id: "tr_" + t.id,
        _traderId: t.id,
        id: t.id,
        name: t.nickname || t.name,
        analystName: t.traderName || t.trader_name || "",
        alphaScore: t.alphaScore || t.alpha_score || 0,
        alphaGrade: t.grade || "D",
        winRate: t.winRate || t.win_rate || 0,
        profitFactor: t.profitFactor || t.profit_factor || 0,
        subscribers: t.totalFollowers || t.copyFollowers || t.copy_followers || 0,
        totalSignals: t.totalTrades || t.total_trades || 0,
        closedSignals: t.totalTrades || t.total_trades || 0,
        avgRR: 0, netPips: 0,
        daysActive: t.daysActiveNum || t.daysActive || 0,
        avgSignalWeek: (t.daysActiveNum > 0) ? ((t.totalTrades || 0) / (t.daysActiveNum / 7)) : 0,
        riskLevel: t.riskLevel || t.risk_level || "MEDIUM",
        totalAUM: t.totalAUM || t.total_aum || 0,
        description: t.broker || "",
        sourceType: ssActive ? "signal_sync" : "live_trader",
        roi: t.roi || 0,
        maxDD: t.maxDD || t.maxDrawdown || t.max_drawdown_pct || 0,
        accountAge: t.accountAge || t.account_age || "",
        platform: t.platform || "",
        flagsJson: "[]",
        signalSyncActive: ssActive,
        planROI: t.planROI || 0,
        planWinRate: t.planWinRate || 0,
        planMaxDD: t.planMaxDD || 0,
        planTotalTrades: t.planTotalTrades || 0,
      };});
      setSets([...signals, ...traders]);
    }).finally(()=>setLoading(false));

    const token = localStorage.getItem("access_token")||localStorage.getItem("token");
    if(token) {
      fetch("/api/investor/copy-trader-subscriptions", { headers: authHeader() })
        .then(r=>r.json()).then(d=>{ if(d.ok) setCopySubs(d.subscriptions||[]); }).catch(()=>{});
    }
  },[]);

  // Fetch traders dengan server-side filter + pagination
  useEffect(()=>{
    setTraderLoading(true);
    const params = new URLSearchParams({
      sort: traderSort,
      limit: 12,
      page: traderPage,
    });
    if(traderRisk !== "ALL") params.append("risk", traderRisk);
    if(traderPlatform !== "ALL") params.append("platform", traderPlatform);
    if(traderSearch) params.append("search", traderSearch);
    fetch(`/api/public/traders?${params}`)
      .then(r=>r.json())
      .then(d=>{
        if(d.ok){
          setTraders(d.traders||[]);
          setTraderTotal(d.total||0);
          setTraderTotalPages(d.totalPages||1);
        }
      })
      .catch(()=>{})
      .finally(()=>setTraderLoading(false));
  },[traderSort, traderPage, traderRisk, traderPlatform, traderSearch]);

  const filtered = sets
    .filter(s => {
      const matchSearch = ( s.name||"").toLowerCase().includes(search.toLowerCase()) ||
                          (s.analystName||"").toLowerCase().includes(search.toLowerCase());
      const matchType = signalType === "ALL" || s._type === signalType || s.sourceType === signalType;
      return matchSearch && matchType;
    })
    .sort((a,b) => { if(sortBy==='alphaScore') return (b.alphaScore||0)-(a.alphaScore||0); if(sortBy==='virtualMaxDDPct') return (a.maxDD||0)-(b.maxDD||0); if(sortBy==='virtualRoiPct') return (b.roi||0)-(a.roi||0); if(sortBy==='totalAUM') return (b.totalAUM||0)-(a.totalAUM||0); return (b.alphaScore||0)-(a.alphaScore||0); });
  const analystTotalPages = Math.max(1, Math.ceil(filtered.length / ANALYST_PAGE_SIZE));
  const analystPageSafe = Math.min(analystPage, analystTotalPages);
  const filteredPage = filtered.slice((analystPageSafe-1)*ANALYST_PAGE_SIZE, analystPageSafe*ANALYST_PAGE_SIZE);

  const S = { bg:"var(--bg)", card:"var(--bg-surface)", card2:"var(--bg-elevated)", border:"var(--border)",
              text:"var(--text-main)", muted:"var(--text-muted)", accent:"var(--accent)" };

  const isSubscribed = (id) => Array.isArray(copySubs) && copySubs.some(x=>x.traderId===id&&x.status==="active");

  const doSubscribe = async () => {
    if(!copyModal) return;
    setCopyBusy(copyModal);
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    const r = await fetch("/api/investor/copy-trader-subscribe",{
      method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},
      body:JSON.stringify({traderAccountId:copyModal,lotMode:copyForm.lotMode,
        lotSize:copyForm.lotSize,riskPercent:copyForm.riskPct,maxLot:copyForm.maxLot,
        copySl:copyForm.copySl,copyTp:copyForm.copyTp})
    }).then(r=>r.json());
    if(r.ok){ setCopySubs(p=>[...p.filter(x=>x.traderId!==copyModal),{traderId:copyModal,status:"active"}]); showToast("Copy trader subscribed!"); }
    else showToast(""+(r.error||"Failed"), false);
    setCopyModal(null); setCopyBusy(null);
  };

  const doUnsubscribe = async (tid) => {
    setCopyBusy(tid);
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    await fetch("/api/investor/copy-trader-unsubscribe",{
      method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},
      body:JSON.stringify({traderAccountId:tid})
    });
    setCopySubs(p=>p.filter(x=>x.traderId!==tid)); setCopyBusy(null);
  };

  const filteredTraders = traders; // server-side filter+sort

  // CORE Modul 8 recommendation templates — Indonesian, plain language.
  // Backend sends raw structured data (Type/Severity/Metric/Threshold/
  // AccountDisplayNames) only, never a pre-written sentence — this function
  // is where that data becomes copy, so wording changes never require a
  // backend redeploy.
  function recommendationText(rec) {
    const names = (rec.AccountDisplayNames || []).map(n => (n||"").trim()).join(" & ");
    switch (rec.Type) {
      case "high_correlation":
        return `${names} move very similarly (${(rec.Metric*100).toFixed(0)}% correlation) — copying both adds less diversification than it may seem.`;
      case "concentration":
        return `Adding ${names} leaves your portfolio concentrated in one asset type (score ${rec.Metric.toFixed(0)}/100). A trader with a different strategy would spread that risk out.`;
      case "high_drawdown":
        return `${names} has a history of significant drawdowns (up to ${(rec.Metric*100).toFixed(0)}% in a realistic worst case) — worth weighing before allocating a large share to it.`;
      case "tail_risk":
        return `${names}'s return pattern is uneven — the real downside risk may be larger than standard stats suggest.`;
      case "insufficient_data":
        return `${names} doesn't have enough trading history yet for a reliable read. Treat the score above as a rough estimate, not a final answer.`;
      default:
        return `${names}: ${rec.Type}`;
    }
  }

  function InfoTip({ text }) {
    const [show, setShow] = React.useState(false);
    return (
      <span style={{ position: "relative", display: "inline-flex", marginLeft: 5 }}>
        <span
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          onClick={(e) => { e.stopPropagation(); setShow(s => !s); }}
          style={{
            width: 13, height: 13, borderRadius: "50%", border: "1px solid var(--text-faint)",
            color: "var(--text-faint)", fontSize: 9, fontWeight: 700, display: "flex",
            alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0,
          }}
        >i</span>
        {show && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
              zIndex: 300, background: "var(--bg-elevated)", border: "1px solid var(--border)",
              borderRadius: 8, padding: "8px 10px", width: 200, fontSize: 11, lineHeight: 1.5,
              color: "var(--text-muted)", fontWeight: 400, boxShadow: "var(--shadow-md)",
              textTransform: "none", letterSpacing: "normal",
            }}
          >{text}</div>
        )}
      </span>
    );
  }

  function severityColor(sev) {
    if (sev === "critical") return "var(--danger)";
    if (sev === "warning") return "var(--warning)";
    return "var(--text-muted)";
  }

  function healthScoreColor(score) {
    if (score >= 70) return "var(--success)";
    if (score >= 40) return "var(--warning)";
    return "var(--danger)";
  }

  function healthScoreLabel(score) {
    if (score >= 70) return "Healthy";
    if (score >= 40) return "Needs Attention";
    return "High Risk";
  }

  async function handleAnalyzePortfolio() {
    setAnalyzeModalOpen(true);
    setAnalyzeLoading(true);
    setAnalyzeError(null);
    setAnalyzeResult(null);
    try {
      const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
      const res = await fetch("/api/investor/analyze-portfolio", {
        method: "POST",
        headers: {"Content-Type":"application/json", Authorization:`Bearer ${token}`},
        body: JSON.stringify({
          accounts: portfolioCart.map(p => ({traderAccountId: p.accountId, allocationAmount: 1})),
          lookbackDays: 90,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to analyze portfolio");
      setAnalyzeResult(data);
    } catch (e) {
      setAnalyzeError(e.message || "Something went wrong");
    } finally {
      setAnalyzeLoading(false);
    }
  }
  return (
    <div style={{ padding: "24px 0", maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-main)", margin: 0 }}>Marketplace</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "4px 0 0" }}>
          Plan-driven strategies ranked by AlphaRank™ — back a plan, not a trade.
        </p>
      </div>
      <div>
        {true && (<>
          <div style={{display:"flex", gap:12, marginBottom:24, flexWrap:"wrap"}}>
            <input value={search} onChange={e=>{setSearch(e.target.value);setAnalystPage(1);}}
              placeholder="Search copy signals or analysts..."
              style={{flex:1, minWidth:200, background:"var(--bg-surface)", border:`1px solid ${S.border}`,
                borderRadius:"var(--radius-md)", color:S.text, fontSize:13, padding:"10px 14px"}}/>
            <select value={sortBy} onChange={e=>{setSortBy(e.target.value);setAnalystPage(1);}}
              style={{background:"var(--bg-surface)", border:`1px solid ${S.border}`, borderRadius:"var(--radius-md)",
                color:S.text, fontSize:13, padding:"10px 14px"}}>
              <option value="alphaScore">Sort: AlphaScore</option>
              <option value="virtualRoiPct">Sort: ROI</option>
              <option value="virtualMaxDDPct">Sort: Lowest DD</option>
              <option value="totalAUM">Sort: AUM Managed</option>
            </select>
          </div>

          {/* ── Marketplace Legend Banner ── */}
          <div style={{
            background:"rgba(78,154,241,0.06)",
            border:"1px solid rgba(78,154,241,0.18)",
            borderRadius:"var(--radius-lg)",
            padding:"12px 18px",
            marginBottom:16,
            display:"flex",
            flexWrap:"wrap",
            gap:"8px 24px",
            alignItems:"center",
          }}>
            <span style={{fontSize:11,color:"var(--text-faint)",fontWeight:700,letterSpacing:"0.04em",textTransform:"uppercase",flexShrink:0}}>Guide</span>
            {[
              ["   SignalSync™", "Signals verified by real account execution — follow actual trades (LIVE) or disciplined plan (PLAN)"],
              ["   Live Trader", "Copy real trades automatically, lot-sized proportional to your account"],
              ["● LIVE", "Mirrors actual trade execution from the trader's real account"],
              ["◆ PLAN", "Follow the trade plan (entry, SL, TP) — without copying the exact lot size"],
              ["   Analyst", "Manual signal provider — publishes trade ideas without a live account connection. ROI & Max DD are simulated: $1,000 · 1.5% risk/trade · Balanced profile"],
            ].map(([term, desc]) => (
              <span key={term} style={{fontSize:11,color:"var(--text-muted)"}}>
                <strong style={{color:"var(--text-main)"}}>{term}</strong>
                <span style={{color:"var(--border)",margin:"0 4px"}}>—</span>
                {desc}
              </span>
            ))}
          </div>

          <div style={{display:"flex", gap:16, marginBottom:24}}>
            {[
              ["Total Strategies", sets.length],
              ["Total Followers", sets.reduce((a,x)=>a+x.subscribers,0)],
              ["Avg Win Rate", sets.length ? (sets.reduce((a,x)=>a+x.winRate,0)/sets.length).toFixed(1)+"%" : "—"],
            ].map(([label,val])=>(
              <div key={label} style={{background:"var(--bg-surface)", border:`1px solid ${S.border}`,
                borderRadius:10, padding:"12px 20px", flex:1, textAlign:"center"}}>
                <div style={{fontSize:20, fontWeight:800, color:S.accent}}>{val}</div>
                <div style={{fontSize:11, color:S.muted, marginTop:2}}>{label}</div>
              </div>
            ))}
          </div>



          {loading ? (
            <div style={{textAlign:"center", padding:60, color:S.muted}}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{textAlign:"center", padding:60, color:S.muted}}>No copy signals found</div>
          ) : (
            <>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:12}}>
              {filteredPage.map((set, idx) => (
                <div key={set.id} style={{
                  background:"var(--bg-surface)", borderRadius:14, padding:20, position:"relative",
                  border:`1px solid ${S.border}`}}>
                  {/* Rank */}
                  <div style={{position:"absolute",top:12,left:12,
                    background:"rgba(255,255,255,0.06)",
                    color:S.muted,
                    borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:800}}>
                    #{idx+1} RANKED
                  </div>

                  <div style={{marginTop:36}}>
                    {/* Source type badge */}
                    {(() => { const b = sourceTypeBadge(set.sourceType); return (
                      <div style={{display:"inline-flex", alignItems:"center", gap:5,
                        marginBottom:10,
                      }}>
                        <div style={{display:"inline-flex", alignItems:"center", gap:4,
                          padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:800,
                          background:b.bg, color:b.color, border:`1px solid ${b.border}`,
                          letterSpacing:"0.04em",
                        }}>{b.label}</div>
                        {set.sourceType==="signal_sync" && (
                          <span
                            title="This provider's trades are executed through a real, verified trading account. You can choose to copy their actual execution (LIVE) or their disciplined plan based on stop-loss & take-profit (PLAN)."
                            style={{
                              display:"inline-flex", alignItems:"center", justifyContent:"center",
                              width:14, height:14, borderRadius:"50%",
                              background:"var(--bg-elevated)", color:"var(--text-faint)",
                              fontSize:9, fontWeight:700, cursor:"help",
                              border:"1px solid var(--border-subtle)",
                            }}
                          >i</span>
                        )}
                      </div>
                    ); })()}
                    {/* Avatar + Name */}
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                      <div style={{width:44,height:44,borderRadius:"50%",
                        background:"var(--bg-elevated-soft)",
                        border:`2px solid ${gradeColor(set.virtualAlphaGrade||set.alphaGrade)}`,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:16,fontWeight:900,color:gradeColor(set.alphaGrade),flexShrink:0}}>
                        {set.virtualAlphaGrade||set.alphaGrade||"?"}
                      </div>
                      <div>
                        <div style={{fontSize:15,fontWeight:800,color:"var(--text-main)"}}>{set.name}</div>
                        <div style={{fontSize:11,color:S.muted}}>{set.analystName}</div>
                        <div style={{fontSize:11,color:S.muted}}>AlphaScore {(set.virtualAlphaScore||set.alphaScore)?.toFixed(1)} · {signalStyle(set.avgSignalWeek, set.sourceType)}</div>
                      </div>
                    </div>

                    {/* Risk badge */}
                    <div style={{display:"inline-block",padding:"2px 10px",borderRadius:6,
                      fontSize:11,fontWeight:700,marginBottom:14,
                      background:"var(--bg-elevated-soft)",color:"var(--text-muted)",
                      border:"1px solid var(--border-subtle)"}}>
                      {riskLabel(set.riskLevel)}
                    </div>

                    {/* ROI/NET PIPS + AUM */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                      <div>
                        {(set.sourceType==="live_trader"||set.sourceType==="signal_sync") ? (
                          <>
                            <div style={{fontSize:10,color:S.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:4}}>ROI</div>
                            <div style={{fontSize:24,fontWeight:900,color:(set.roi||0)>=0?"var(--success)":"var(--danger)"}}>
                              {(set.roi||0)>=0?"+":""}{(set.roi||0).toFixed(1)}%
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{fontSize:10,color:S.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:4}}>ROI</div>
                            <div style={{fontSize:24,fontWeight:900,color:(set.virtualRoiPct||0)>=0?"var(--success)":"var(--danger)"}}>
                              {(set.virtualRoiPct||0)>=0?"+":""}{(set.virtualRoiPct||0).toFixed(1)}%
                            </div>
                          </>
                        )}
                      </div>
                      <div>
                        <div style={{fontSize:10,color:S.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:4}}>AUM MANAGED</div>
                        <div style={{fontSize:24,fontWeight:900,color:"var(--text-main)"}}>
                          {set.totalAUM>0?"$"+(set.totalAUM).toFixed(0):"—"}
                        </div>
                      </div>
                    </div>

                    {/* Stats row */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
                      {set.signalSyncActive&&(
                        <div style={{gridColumn:"1 / -1", display:"flex", flexDirection:"column", gap:6, marginBottom:10}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,
                            padding:"5px 10px",borderRadius:6,background:"rgba(34,197,94,0.07)",
                            border:"1px solid rgba(34,197,94,0.2)"}}>
                            <span style={{fontSize:10,color:"var(--success)",fontWeight:700}}>● LIVE</span>
                            <span style={{fontSize:10,color:"var(--text-muted)"}}>
                              ROI: <strong style={{color:(set.roi||0)>=0?"var(--success)":"var(--danger)"}}>{(set.roi||0)>=0?"+":""}{(set.roi||0).toFixed(1)}%</strong>
                              {" "}WR: <strong style={{color:"var(--text-main)"}}>{(set.winRate||0).toFixed(0)}%</strong>
                              {" "}DD: <strong style={{color:"var(--warning)"}}>{(set.maxDD||0).toFixed(1)}%</strong>
                            </span>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:6,
                            padding:"5px 10px",borderRadius:6,background:"rgba(78,154,241,0.07)",
                            border:"1px solid rgba(78,154,241,0.2)"}}>
                            <span style={{fontSize:10,color:"var(--accent)",fontWeight:700}}>◆ PLAN</span>
                            <span style={{fontSize:10,color:"var(--text-muted)"}}>
                              ROI: <strong style={{color:(set.planROI||0)>=0?"var(--success)":"var(--danger)"}}>{(set.planROI||0)>=0?"+":""}{(set.planROI||0).toFixed(1)}%</strong>
                              {" "}WR: <strong style={{color:"var(--text-main)"}}>{(set.planWinRate||0).toFixed(0)}%</strong>
                              {" "}DD: <strong style={{color:"var(--warning)"}}>{(set.planMaxDD||0).toFixed(1)}%</strong>
                            </span>
                          </div>
                        </div>
                      )}
                      {(()=>{
                        const dd=(set.sourceType==="manual_analyst"?set.virtualMaxDDPct:set.maxDD)||0;
                        const ddColor=dd>20?"var(--danger)":dd>10?"var(--warning)":"var(--success)";
                        return [
                          ["Win Rate",     (set.winRate||0).toFixed(0)+"%",        "var(--text-main)"],
                          ["Max DD",       dd.toFixed(1)+"%",                       ddColor],
                          ["Profit Factor",(set.profitFactor||0).toFixed(2),       "var(--text-main)"],
                          ["Followers",    String(set.subscribers||0),              "var(--text-main)"],
                        ].map(([l,v,c])=>(
                          <div key={l} style={{textAlign:"center"}}>
                            <div style={{fontSize:14,fontWeight:800,color:c}}>{v}</div>
                            <div style={{fontSize:10,color:S.muted}}>{l}</div>
                          </div>
                        ));
                      })()}
                    </div>

                    {/* Footer */}
                    <div style={{fontSize:11,color:S.muted,marginBottom:14}}>
                      {(set._type==="live_trader"||set._type==="signal_sync")
                        ? `${set.platform||""} · Trading for ${set.accountAge||"?"} · ${set.totalSignals||0} trades`
                        : `Active ${set.daysActive||0}d · ${set.totalSignals||0} signals · ${(set.avgSignalWeek||0).toFixed(1)}/week`
                      }
                    </div>

                    <div style={{display:"flex",gap:8,marginBottom:8}}>
                      {(set._type==="live_trader"||set._type==="signal_sync") && (
                        (() => {
                          const inCart = portfolioCart.some(p => p.accountId === set._traderId);
                          return (
                            <button onClick={()=>{
                                if (inCart) {
                                  setPortfolioCart(prev => prev.filter(p => p.accountId !== set._traderId));
                                } else {
                                  setPortfolioCart(prev => [...prev, {accountId: set._traderId, name: set.name}]);
                                }
                              }}
                              style={{flex:1,padding:"8px 0",borderRadius:8,
                                border: inCart ? "1px solid var(--success)" : "1px solid var(--border)",
                                background: inCart ? "var(--success-soft)" : "var(--bg-elevated)",
                                color: inCart ? "var(--success)" : "var(--text-muted)",
                                fontSize:12,fontWeight:600,cursor:"pointer"}}>
                              {inCart ? "✓ Added" : "+ Add to Portfolio"}
                            </button>
                          );
                        })()
                      )}
                      <button onClick={()=>{
                          const baseUrl = (set._type==="live_trader"||set._type==="signal_sync")
                            ? `${window.location.origin}/strategy/${set._traderId}`
                            : `${window.location.origin}/strategy/analyst/${set._setId||set.id}`;
                          const url = affCode ? `${baseUrl}?ref=${affCode}` : baseUrl;
                          navigator.clipboard.writeText(url);
                          alert("Link copied!\n\n" + url);
                        }}
                        style={{flex:1,padding:"8px 0",borderRadius:8,border:"1px solid var(--accent-border)",
                          background:"var(--accent-soft)",color:"var(--accent)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                        Share & Earn
                      </button>
                    </div>
                    <button onClick={()=>{
                        if(set._type==="live_trader"||set._type==="signal_sync") navigate(`/trader/${set._traderId}`);
                        else navigate(`/marketplace/analyst/${set._setId||set.id}`);
                      }}
                      style={{width:"100%",padding:"10px 0",borderRadius:8,border:"none",
                        background:"transparent",color:"var(--accent)",fontSize:13,fontWeight:700,
                        cursor:"pointer",borderTop:"1px solid var(--border-subtle)",paddingTop:12}}>
                      View Strategy →
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Analyst Pagination */}
            {analystTotalPages > 1 && (
              <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:8,marginTop:24,flexWrap:"wrap"}}>
                <button onClick={()=>setAnalystPage(1)} disabled={analystPageSafe===1}
                  style={{padding:"6px 10px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                    color:S.text,borderRadius:6,cursor:analystPageSafe===1?"not-allowed":"pointer",fontSize:12,opacity:analystPageSafe===1?0.4:1}}>«</button>
                <button onClick={()=>setAnalystPage(p=>Math.max(1,p-1))} disabled={analystPageSafe===1}
                  style={{padding:"6px 12px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                    color:S.text,borderRadius:6,cursor:analystPageSafe===1?"not-allowed":"pointer",fontSize:12,opacity:analystPageSafe===1?0.4:1}}>‹ Prev</button>
                {Array.from({length:analystTotalPages},(_,i)=>i+1)
                  .filter(p=>p===1||p===analystTotalPages||Math.abs(p-analystPageSafe)<=1)
                  .reduce((acc,p,i,arr)=>{if(i>0&&p-arr[i-1]>1)acc.push('...');acc.push(p);return acc;},[])
                  .map((p,i)=>p==='...'?(
                    <span key={"e"+i} style={{color:S.muted,padding:"0 4px"}}>...</span>
                  ):(
                    <button key={p} onClick={()=>setAnalystPage(p)}
                      style={{padding:"6px 12px",
                        background:p===analystPageSafe?S.accent:"rgba(255,255,255,0.05)",
                        border:`1px solid ${p===analystPageSafe?S.accent:"rgba(255,255,255,0.1)"}`,
                        color:"#fff",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:p===analystPageSafe?700:400}}>
                      {p}
                    </button>
                  ))}
                <button onClick={()=>setAnalystPage(p=>Math.min(analystTotalPages,p+1))} disabled={analystPageSafe===analystTotalPages}
                  style={{padding:"6px 12px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                    color:S.text,borderRadius:6,cursor:analystPageSafe===analystTotalPages?"not-allowed":"pointer",fontSize:12,opacity:analystPageSafe===analystTotalPages?0.4:1}}>Next ›</button>
                <button onClick={()=>setAnalystPage(analystTotalPages)} disabled={analystPageSafe===analystTotalPages}
                  style={{padding:"6px 10px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                    color:S.text,borderRadius:6,cursor:analystPageSafe===analystTotalPages?"not-allowed":"pointer",fontSize:12,opacity:analystPageSafe===analystTotalPages?0.4:1}}>»</button>
                <span style={{fontSize:12,color:S.muted,marginLeft:8}}>
                  Page {analystPageSafe} of {analystTotalPages} · {filtered.length} strategies
                </span>
              </div>
            )}
            </>
          )}
        </>)}

        {false && (
          <div>
            <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
              {[
                {l:"Total Traders",v:traderTotal||traders.length},
                {l:"Active Copies",v:copySubs.filter(x=>x.status==="active").length},
                {l:"Avg AlphaScore",v:traders.length?(traders.reduce((a,t)=>a+(t.alphaScore||t.alpha_score||0),0)/traders.length).toFixed(1):"—"},
                {l:"Avg Win Rate",v:traders.length?(traders.reduce((a,t)=>a+(t.winRate||t.win_rate||0),0)/traders.length).toFixed(1)+"%":"—"},
              ].map(x=>(
                <div key={x.l} style={{flex:1,minWidth:120,background:"rgba(255,255,255,0.04)",
                  border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:"12px 16px"}}>
                  <div style={{fontSize:11,color:S.muted,fontWeight:600}}>{x.l}</div>
                  <div style={{fontSize:20,fontWeight:800,color:S.text}}>{x.v}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
              <input value={traderSearch}
                onChange={e=>{ setTraderSearch(e.target.value); setTraderPage(1); }}
                placeholder="Search trader, broker..."
                style={{flex:2,minWidth:180,background:"rgba(255,255,255,0.04)",
                  border:"1px solid var(--border)",color:"var(--text-main)",
                  borderRadius:8,padding:"8px 12px",fontSize:13}} />
              <select value={traderSort}
                onChange={e=>{ setTraderSort(e.target.value); setTraderPage(1); }}
                style={{background:"var(--bg-elevated)",border:"1px solid var(--border)",
                  color:"var(--text-main)",borderRadius:8,padding:"8px 12px",fontSize:13}}>
                <option value="alpha_score">Sort: AlphaScore</option>
                <option value="roi">Sort: ROI</option>
                <option value="win_rate">Sort: Win Rate</option>
                <option value="profit_factor">Sort: Profit Factor</option>
                <option value="drawdown">Sort: Lowest DD</option>
                <option value="trades">Sort: Most Trades</option>
              </select>
              <select value={traderRisk}
                onChange={e=>{ setTraderRisk(e.target.value); setTraderPage(1); }}
                style={{background:"var(--bg-elevated)",border:"1px solid var(--border)",
                  color:"var(--text-main)",borderRadius:8,padding:"8px 12px",fontSize:13}}>
                <option value="ALL">Risk: All</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="EXTREME">Extreme</option>
              </select>
              <select value={traderPlatform}
                onChange={e=>{ setTraderPlatform(e.target.value); setTraderPage(1); }}
                style={{background:"var(--bg-elevated)",border:"1px solid var(--border)",
                  color:"var(--text-main)",borderRadius:8,padding:"8px 12px",fontSize:13}}>
                <option value="ALL">Platform: All</option>
                <option value="MT4">MT4</option>
                <option value="MT5">MT5</option>
              </select>
              {traderLoading && <span style={{fontSize:12,color:S.muted}}>Loading...</span>}
            </div>
            {traders.length === 0 && !traderLoading && (
              <div style={{textAlign:"center",padding:40,color:S.muted}}>
                <div style={{fontSize:32,marginBottom:12}}></div>
                <div style={{fontSize:14}}>No traders found matching your criteria</div>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16}}>
              {filteredTraders.map((t,i)=>{
                const gc=t.grade?.startsWith("A")?"var(--success)":t.grade?.startsWith("B")?"#4e9af1":t.grade?.startsWith("C")?"var(--warning)":"var(--danger)";
                const sub=isSubscribed(t.id);
                return (
                  <div key={t.id} onClick={()=>navigate(`/trader/${t.id}`)}
                  style={{background:"rgba(255,255,255,0.03)",
                    border:`1px solid ${sub?"#4e9af1":"rgba(255,255,255,0.07)"}`,
                    borderRadius:16,overflow:"hidden",position:"relative",cursor:"pointer",
                    transition:"border-color 0.2s,transform 0.2s",
                  }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor="#4e9af144";e.currentTarget.style.transform="translateY(-2px)";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=sub?"#4e9af1":"rgba(255,255,255,0.07)";e.currentTarget.style.transform="translateY(0)";}}
                >
                  {/* Top strip — rank + copying only */}
                  {(i<3 || sub) && <div style={{
                    padding:"5px 16px",
                    background:"rgba(255,255,255,0.02)",
                    borderBottom:"1px solid rgba(255,255,255,0.06)",
                    display:"flex",alignItems:"center",justifyContent:"space-between"
                  }}>
                    {i<3&&<span style={{fontSize:10,fontWeight:700,
                      color:"var(--text-muted)",letterSpacing:0.5}}>
                      #{i+1} RANKED
                    </span>}
                    {sub&&<span style={{fontSize:10,fontWeight:700,color:"#4e9af1"}}>● COPYING</span>}
                  </div>}

                  <div style={{padding:"14px 16px"}}>
                    {/* Header: Grade circle + Name */}
                    <div style={{display:"flex",gap:14,alignItems:"center",marginBottom:16}}>
                      <div style={{
                        width:52,height:52,borderRadius:"50%",
                        border:`2px solid ${gc}`,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        flexShrink:0,position:"relative"
                      }}>
                        <span style={{fontSize:20,fontWeight:900,color:gc}}>{t.grade||"?"}</span>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          <div style={{fontSize:14,fontWeight:800,color:S.text,
                            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                            {t.nickname||"Trader"}
                          </div>
                          {t.role==="both" && (
                            <span style={{fontSize:9,fontWeight:700,padding:"2px 6px",
                              borderRadius:4,background:"rgba(251,191,36,0.15)",
                              color:"var(--warning)",border:"1px solid rgba(251,191,36,0.3)",
                              whiteSpace:"nowrap",flexShrink:0}}>
                              COPY TRADER
                            </span>
                          )}
                        </div>
                        <div style={{fontSize:11,color:S.muted,marginTop:2}}>
                          {t.riskLevel||"MEDIUM"} Risk · {t.platform}
                          
                        </div>
                        <div style={{fontSize:11,color:"#8c92b0",marginTop:2}}>
                          AlphaScore <span style={{color:gc,fontWeight:800}}>{(t.alphaScore||t.alpha_score||0).toFixed(1)}</span>
                          <span style={{color:"#8c92b0",fontWeight:400}}> · {scoreDesc(t.alphaScore||t.alpha_score||0)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Strategy storytelling */}
                    {(() => { const s = strategyStory(t); return (
                      <div style={{marginBottom:12,padding:"8px 12px",borderRadius:8,
                        background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)"}}>
                        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                          {[s.riskTag].map((tag,i)=>(
                            <span key={i} style={{fontSize:10,color:"var(--text-muted)",
                              background:"rgba(255,255,255,0.04)",
                              padding:"2px 8px",borderRadius:20,
                              border:"1px solid rgba(255,255,255,0.08)"}}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    ); })()}

                    {/* Main metric: ROI prominent but not aggressive */}
                    <div style={{
                      background:"rgba(255,255,255,0.03)",borderRadius:10,
                      padding:"10px 14px",marginBottom:12,
                      display:"flex",justifyContent:"space-between",alignItems:"center"
                    }}>
                      <div>
                        <div style={{fontSize:10,color:S.muted,fontWeight:600,marginBottom:2}}>RETURN</div>
                        <div style={{fontSize:22,fontWeight:900,
                          color:"var(--text-main)",letterSpacing:-0.5}}>
                          {(t.roi||0)>=0?"+":""}{(t.roi||0).toFixed(1)}%
                        </div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:10,color:S.muted,fontWeight:600,marginBottom:2}}>AUM MANAGED</div>
                        <div style={{fontSize:16,fontWeight:800,color:"var(--text-muted)"}}>
                          {t.totalAUM ? "$"+(t.totalAUM).toFixed(0) : "—"}
                        </div>
                      </div>
                    </div>

                    {/* Secondary metrics — muted, small */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:14}}>
                      {[
                        {l:"Consistency",v:(t.winRate||t.win_rate||0).toFixed(0)+"%"},
                        {l:"Profit Factor",v:(t.profitFactor||t.profit_factor||0).toFixed(2)},
                        {l:"Max DD",v:(t.maxDD||t.max_drawdown_pct||0).toFixed(1)+"%"},
                      ].map(x=>(
                        <div key={x.l} style={{textAlign:"center",padding:"6px 4px",
                          background:"rgba(255,255,255,0.02)",borderRadius:6}}>
                          <div style={{fontSize:12,fontWeight:700,color:S.text}}>{x.v}</div>
                          <div style={{fontSize:9,color:S.muted,marginTop:1}}>{x.l}</div>
                        </div>
                      ))}
                    </div>

                    {/* Footer: CTA */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                      <span style={{fontSize:11,color:S.muted}}>
                        {t.accountAge && t.accountAge !== "New" ? "Trading for "+t.accountAge+" · " : ""}{t.totalTrades||t.total_trades||0} trades
                      </span>
                      <span style={{fontSize:12,fontWeight:700,color:"#4e9af1"}}>
                        View Strategy →
                      </span>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Pagination */}
            {traderTotalPages > 1 && (
              <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:8,marginTop:24}}>
                <button onClick={()=>setTraderPage(1)} disabled={traderPage===1}
                  style={{padding:"6px 10px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                    color:S.text,borderRadius:6,cursor:traderPage===1?"not-allowed":"pointer",fontSize:12}}>«</button>
                <button onClick={()=>setTraderPage(p=>Math.max(1,p-1))} disabled={traderPage===1}
                  style={{padding:"6px 12px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                    color:S.text,borderRadius:6,cursor:traderPage===1?"not-allowed":"pointer",fontSize:12}}>‹ Prev</button>
                {Array.from({length:traderTotalPages},(_,i)=>i+1)
                  .filter(p=>p===1||p===traderTotalPages||Math.abs(p-traderPage)<=1)
                  .reduce((acc,p,i,arr)=>{
                    if(i>0&&p-arr[i-1]>1) acc.push('...');
                    acc.push(p); return acc;
                  },[])
                  .map((p,i)=>p==='...'?(
                    <span key={`e${i}`} style={{color:S.muted,padding:"0 4px"}}>...</span>
                  ):(
                    <button key={p} onClick={()=>setTraderPage(p)}
                      style={{padding:"6px 12px",
                        background:p===traderPage?S.accent:"rgba(255,255,255,0.05)",
                        border:`1px solid ${p===traderPage?S.accent:"rgba(255,255,255,0.1)"}`,
                        color:"#fff",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:p===traderPage?700:400}}>
                      {p}
                    </button>
                  ))}
                <button onClick={()=>setTraderPage(p=>Math.min(traderTotalPages,p+1))} disabled={traderPage===traderTotalPages}
                  style={{padding:"6px 12px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                    color:S.text,borderRadius:6,cursor:traderPage===traderTotalPages?"not-allowed":"pointer",fontSize:12}}>Next ›</button>
                <button onClick={()=>setTraderPage(traderTotalPages)} disabled={traderPage===traderTotalPages}
                  style={{padding:"6px 10px",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                    color:S.text,borderRadius:6,cursor:traderPage===traderTotalPages?"not-allowed":"pointer",fontSize:12}}>»</button>
                <span style={{fontSize:12,color:S.muted,marginLeft:8}}>
                  Page {traderPage} of {traderTotalPages} ({traderTotal} traders)
                </span>
              </div>
            )}

            {copyModal&&(
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:1000,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                <div style={{background:"var(--bg-surface)",borderRadius:14,border:"1px solid rgba(255,255,255,0.1)",
                  padding:"28px 32px",minWidth:320,maxWidth:400}}>
                  <div style={{fontSize:15,fontWeight:800,color:S.text,marginBottom:4}}> Copy Trader Settings</div>
                  <div style={{marginBottom:12}}>
                    <select value={copyForm.lotMode} onChange={e=>setCopyForm(f=>({...f,lotMode:e.target.value}))}
                      style={{width:"100%",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                      color:S.text,borderRadius:8,padding:"8px 12px",fontSize:13}}>
                      <option value="FIXED">Fixed Lot</option>
                      <option value="RISK_PCT">Risk % of Balance</option>
                    </select>
                  </div>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={doSubscribe} disabled={!!copyBusy} style={{flex:1,padding:"10px",background:S.accent,
                      color:"#fff",border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                       Start Copying
                    </button>
                    <button onClick={()=>setCopyModal(null)} style={{flex:1,padding:"10px",
                      background:"rgba(255,255,255,0.05)",color:S.muted,
                      border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>



      {/* CORE Portfolio Analyzer — floating cart bar with removable chips */}
      {portfolioCart.length > 0 && !analyzeModalOpen && (
        <div style={{position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", zIndex:1900,
          background:"var(--bg-surface)", border:"1px solid var(--border)", borderRadius:12,
          padding:"12px 16px", boxShadow:"var(--shadow-md)", display:"flex", alignItems:"center", gap:12,
          maxWidth:"90vw", flexWrap:"wrap"}}>

          <div style={{display:"flex", gap:6, flexWrap:"wrap", maxWidth:480}}>
            {portfolioCart.map(p => (
              <span key={p.accountId} style={{display:"flex", alignItems:"center", gap:5,
                background:"var(--bg-elevated)", border:"1px solid var(--border)",
                borderRadius:999, padding:"4px 6px 4px 10px", fontSize:12, color:"var(--text-main)"}}>
                {p.name}
                <button onClick={()=>setPortfolioCart(prev => prev.filter(x => x.accountId !== p.accountId))}
                  style={{background:"transparent", border:"none", color:"var(--text-faint)",
                    cursor:"pointer", fontSize:14, lineHeight:1, padding:"0 2px"}}>
                  ×
                </button>
              </span>
            ))}
          </div>

          <button onClick={()=>setPortfolioCart([])}
            style={{background:"transparent", border:"none", color:"var(--text-muted)",
              fontSize:12, cursor:"pointer", textDecoration:"underline", whiteSpace:"nowrap"}}>
            Clear all
          </button>
          <button onClick={handleAnalyzePortfolio}
            style={{padding:"8px 18px", borderRadius:8, border:"none",
              background:"var(--accent)", color:"var(--text-on-accent)",
              fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap"}}>
            Analyze Portfolio
          </button>
        </div>
      )}

      {/* CORE Portfolio Analyzer — result modal */}
      {analyzeModalOpen && (
        <div style={{position:"fixed", inset:0, zIndex:2100, background:"rgba(0,0,0,0.6)",
          display:"flex", alignItems:"center", justifyContent:"center", padding:20}}
          onClick={()=>setAnalyzeModalOpen(false)}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:"var(--bg-surface)", border:"1px solid var(--border)", borderRadius:16,
              maxWidth:560, width:"100%", maxHeight:"85vh", overflowY:"auto", padding:24}}>

            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16}}>
              <div style={{fontSize:16, fontWeight:700, color:"var(--text-main)"}}>Portfolio Analysis</div>
              <button onClick={()=>setAnalyzeModalOpen(false)}
                style={{background:"transparent", border:"none", color:"var(--text-muted)",
                  fontSize:20, cursor:"pointer", lineHeight:1}}>×</button>
            </div>

            {analyzeLoading && (
              <div style={{textAlign:"center", padding:"40px 0", color:"var(--text-muted)", fontSize:13}}>
                Analyzing portfolio...
              </div>
            )}

            {analyzeError && (
              <div style={{padding:14, borderRadius:8, background:"var(--danger-soft)",
                border:"1px solid var(--danger)", color:"var(--danger)", fontSize:13}}>
                {analyzeError}
              </div>
            )}

            {analyzeResult && (
              <>
                {(analyzeResult.portfolioRisk?.Accounts?.length || 0) < 2 ? (
                  <div style={{padding:14, borderRadius:8, marginBottom:16,
                    background:"var(--bg-elevated)", border:"1px solid var(--border)",
                    color:"var(--text-muted)", fontSize:12, lineHeight:1.6}}>
                    Add at least one more trader to see a combined Portfolio Health score — a single trader can't show correlation or diversification on its own.
                  </div>
                ) : (
                  <>
                    <div style={{textAlign:"center", marginBottom:20, padding:20,
                      background:"var(--bg-elevated)", borderRadius:12}}>
                      <div style={{fontSize:10, color:"var(--text-faint)", fontWeight:700,
                        textTransform:"uppercase", letterSpacing:0.8, marginBottom:6}}>Portfolio Health</div>
                      <div style={{fontSize:36, fontWeight:900, color:healthScoreColor(analyzeResult.healthScore)}}>
                        {analyzeResult.healthScore.toFixed(0)}
                        <span style={{fontSize:16, color:"var(--text-faint)"}}>/100</span>
                      </div>
                      <div style={{fontSize:12, fontWeight:700, marginTop:6, color:healthScoreColor(analyzeResult.healthScore)}}>
                        {healthScoreLabel(analyzeResult.healthScore)}
                      </div>
                    </div>

                    {analyzeResult.dataQuality !== "full" && (
                      <div style={{padding:12, borderRadius:8, marginBottom:16,
                        background:"var(--warning-soft)", border:"1px solid var(--warning)",
                        color:"var(--warning)", fontSize:12}}>
                        {analyzeResult.dataQuality === "insufficient"
                          ? "Most of the data isn't sufficient yet — this score isn't fully reliable."
                          : "Some data is still limited — this score may not fully reflect your portfolio."}
                      </div>
                    )}
                  </>
                )}

                <div style={{display:"flex", alignItems:"center", fontSize:12, fontWeight:700, color:"var(--text-faint)",
                  textTransform:"uppercase", letterSpacing:0.7, marginBottom:4}}>
                  Portfolio Insights
                  <InfoTip text="This looks at how these traders work together as a portfolio — not a rating of any single trader. For a trader's full track record, visit their profile page."/>
                </div>
                <div style={{fontSize:11, color:"var(--text-faint)", marginBottom:10, lineHeight:1.5}}>
                  Based on combining these traders — not an individual performance rating.
                </div>

                {(!analyzeResult.recommendations || analyzeResult.recommendations.length === 0) && (
                  <div style={{fontSize:13, color:"var(--text-muted)", padding:"12px 0"}}>
                    No significant risk findings for this combination.
                  </div>
                )}

                {(analyzeResult.recommendations || []).map((rec, i) => (
                  <div key={i} style={{padding:"10px 12px", borderRadius:8, marginBottom:8,
                    background:"var(--bg-elevated)", borderLeft:`3px solid ${severityColor(rec.Severity)}`,
                    fontSize:13, color:"var(--text-main)", lineHeight:1.5}}>
                    {recommendationText(rec)}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div style={{position:"fixed", bottom:24, right:24, zIndex:2000,
          background: toast.ok ? "var(--success)" : "var(--danger)",
          color:"#fff", padding:"12px 20px", borderRadius:10,
          fontWeight:700, fontSize:13, boxShadow:"0 4px 20px rgba(0,0,0,0.3)"}}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}